import { memo, useEffect, useRef, useState } from "react";
import { formatMoney } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";

const DURATION = 1500;

interface IcmAnimatedMoneyProps {
  value: number;
  className?: string;
  centsClassName?: string;
}

interface IcmMoneyDisplayProps {
  dollars: string;
  cents: string;
  className?: string;
  centsClassName?: string;
}

export const splitFormattedMoney = (formatted: string) => {
  const dot = formatted.lastIndexOf(".");
  return {
    dollars: dot !== -1 ? formatted.slice(0, dot) : formatted,
    cents: dot !== -1 ? formatted.slice(dot + 1) : "00",
  };
};

export const IcmMoneyDisplay = ({
  dollars,
  cents,
  className,
  centsClassName,
}: IcmMoneyDisplayProps) => (
  <span className={cn("inline-flex items-start", className)}>
    <span className="leading-none">{dollars}</span>
    <span className={cn("ml-[0.05em] self-start leading-none", centsClassName)}>
      .{cents}
    </span>
  </span>
);

export const IcmAnimatedMoney = memo(
  ({ value, className, centsClassName }: IcmAnimatedMoneyProps) => {
    const [display, setDisplay] = useState(0);
    const rafRef = useRef<number | null>(null);
    const displayRef = useRef(0);

    useEffect(() => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      const start = performance.now();
      const from = displayRef.current;

      const tick = (now: number) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / DURATION, 1);
        const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        const next = from + (value - from) * eased;
        displayRef.current = next;
        setDisplay(next);
        if (progress < 1) rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
      return () => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      };
    }, [value]);

    const formatted = formatMoney(display.toFixed(2)) ?? "";
    const { dollars, cents } = splitFormattedMoney(formatted);

    return (
      <IcmMoneyDisplay
        dollars={dollars}
        cents={cents}
        className={cn("tabular-nums", className)}
        centsClassName={centsClassName}
      />
    );
  },
);
